import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Paper,
} from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { CoordinateGrid } from '../coordinateGrid';
import {
  parseFactoredPolynomial,
  generateGraphVariations,
  canUseYInterceptMode,
  polynomialsToCurveElements,
  defaultPolynomialGraphConfig,
  suggestXMin,
  suggestXMax,
  suggestYMin,
  suggestYMax,
  equationToLatex,
} from './polynomialGraphUtils';

/**
 * Resolve the intercept type actually used for generation. A polynomial that
 * passes through the origin can't support y-intercept mode (see
 * canUseYInterceptMode), so fall back to x-intercepts. Returned value is also
 * persisted to config, keeping interceptType consistent with the graphs shown.
 */
const resolveInterceptType = (polynomial, requestedType) =>
  requestedType === 'y' && !canUseYInterceptMode(polynomial)
    ? 'x'
    : requestedType || 'x';

/**
 * Admin component for creating/editing polynomial graph questions
 *
 * Props:
 * - config: PolynomialGraphConfig - Current configuration
 * - onConfigChange: (config) => void - Called when config changes
 * - correctAnswer: { correctGraphIndex: number } - Correct answer
 * - onAnswerChange: (answer) => void - Called when answer changes
 */
const PolynomialGraphAdmin = ({
  config = defaultPolynomialGraphConfig,
  onConfigChange,
  correctAnswer,
  onAnswerChange,
}) => {
  const [equationInput, setEquationInput] = useState(config.equation || '');
  const [parseError, setParseError] = useState('');
  const [variations, setVariations] = useState([]);
  const [gridError, setGridError] = useState('');

  // Keep latest callbacks in refs so effects always call the current version
  const onConfigChangeRef = useRef(onConfigChange);
  onConfigChangeRef.current = onConfigChange;
  const onAnswerChangeRef = useRef(onAnswerChange);
  onAnswerChangeRef.current = onAnswerChange;

  // Compute parsed data from equation for display — independent of parent round-trip
  // Server validation keeps factors (root/multiplicity) and degree, but strips
  // leadingCoefficient and yIntercept. Parse from equation when those are missing.
  const parsedPolynomialDisplay = useMemo(() => {
    const hasFullParsedData = config.parsedPolynomial?.leadingCoefficient !== undefined;
    if (hasFullParsedData) return config.parsedPolynomial;

    const eq = equationInput || config.equation;
    if (!eq) return config.parsedPolynomial;
    try {
      return parseFactoredPolynomial(eq);
    } catch {
      return config.parsedPolynomial;
    }
  }, [equationInput, config.equation, config.parsedPolynomial]);

  // A polynomial through the origin (root at 0) can't support a y-intercept
  // question — disable that option and explain why.
  const yInterceptModeBlocked = useMemo(
    () => !!parsedPolynomialDisplay && !canUseYInterceptMode(parsedPolynomialDisplay),
    [parsedPolynomialDisplay]
  );

  // STATE RESTORATION + AUTO-PARSE on mount
  useEffect(() => {
    if (!config.equation) return;

    const hasFullParsedData = config.parsedPolynomial?.leadingCoefficient !== undefined;

    if (hasFullParsedData && config.variationSeed) {
      // Existing fully-parsed config — just regenerate variations. A legacy
      // config may hold interceptType 'y' with a through-origin polynomial;
      // resolve to 'x' so restoration can't hit the y-mode guard.
      setEquationInput(config.equation);
      try {
        const effectiveType = resolveInterceptType(config.parsedPolynomial, config.interceptType);
        const { variations: vars, correctIndex } = generateGraphVariations(
          config.parsedPolynomial,
          effectiveType,
          config.variationSeed
        );
        setVariations(vars);
        setParseError('');
        if (effectiveType !== (config.interceptType || 'x')) {
          onConfigChangeRef.current({
            ...config,
            interceptType: effectiveType,
            correctGraphIndex: correctIndex,
          });
          onAnswerChangeRef.current({ correctGraphIndex: correctIndex });
        }
      } catch (e) {
        setParseError('Failed to restore question configuration');
      }
    } else {
      // Equation present but missing full parsed data or seed — auto-parse and persist
      setEquationInput(config.equation);
      try {
        const parsed = parseFactoredPolynomial(config.equation);
        const seed = Date.now();
        const effectiveType = resolveInterceptType(parsed, config.interceptType);
        const { variations: vars, correctIndex } = generateGraphVariations(
          parsed,
          effectiveType,
          seed
        );
        setVariations(vars);
        setParseError('');
        onConfigChangeRef.current({
          ...config,
          equation: config.equation,
          parsedPolynomial: parsed,
          interceptType: effectiveType,
          variationSeed: seed,
          correctGraphIndex: correctIndex,
        });
        onAnswerChangeRef.current({ correctGraphIndex: correctIndex });
      } catch (e) {
        setParseError(e.message);
      }
    }
  }, []); // Only run on mount

  // Parse equation when input changes
  const handleEquationChange = useCallback((value) => {
    setEquationInput(value);

    if (!value.trim()) {
      setParseError('');
      setVariations([]);
      return;
    }

    try {
      const parsed = parseFactoredPolynomial(value);
      setParseError('');

      // A new equation may pass through the origin, disallowing y-intercept
      // mode; resolve to a valid type before generating.
      const effectiveType = resolveInterceptType(parsed, config.interceptType);

      // Generate variations with new seed
      const seed = Date.now();
      const { variations: vars, correctIndex } = generateGraphVariations(
        parsed,
        effectiveType,
        seed
      );
      setVariations(vars);

      // Update config
      onConfigChange({
        ...config,
        equation: value,
        parsedPolynomial: parsed,
        interceptType: effectiveType,
        variationSeed: seed,
        correctGraphIndex: correctIndex,
      });

      // Update answer
      onAnswerChange({ correctGraphIndex: correctIndex });

    } catch (e) {
      setParseError(e.message);
      setVariations([]);
    }
  }, [config, onConfigChange, onAnswerChange]);

  // Regenerate variations when intercept type changes
  const handleInterceptTypeChange = useCallback((event) => {
    const newType = event.target.value;

    if (config.parsedPolynomial) {
      // Defensive: the Y-intercept option is disabled for through-origin
      // polynomials, but resolve anyway so we never call the generator with an
      // unsupported type.
      const effectiveType = resolveInterceptType(config.parsedPolynomial, newType);
      const seed = Date.now();
      const { variations: vars, correctIndex } = generateGraphVariations(
        config.parsedPolynomial,
        effectiveType,
        seed
      );
      setVariations(vars);

      onConfigChange({
        ...config,
        interceptType: effectiveType,
        variationSeed: seed,
        correctGraphIndex: correctIndex,
      });

      onAnswerChange({ correctGraphIndex: correctIndex });
    } else {
      onConfigChange({
        ...config,
        interceptType: newType,
      });
    }
  }, [config, onConfigChange, onAnswerChange]);

  // Handle grid config changes with validation
  const handleGridChange = useCallback((key, value) => {
    const newGrid = {
      ...config.grid,
      [key]: value,
    };

    // Validate grid bounds
    if (newGrid.xMin >= newGrid.xMax) {
      setGridError('X Min must be less than X Max');
    } else if (newGrid.yMin >= newGrid.yMax) {
      setGridError('Y Min must be less than Y Max');
    } else {
      setGridError('');
    }

    onConfigChange({
      ...config,
      grid: newGrid,
    });
  }, [config, onConfigChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Section 1: Equation Input */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          1. Enter Polynomial Equation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the polynomial in factored form. Examples:
          <br />
          - f(x) = (x+3)(x-2)
          <br />
          - f(x) = 1/6(x+3)(x-2)^2
          <br />
          - f(x) = -(x+1)^2(x-3)
          <br />
          - f(x) = -x(x-3)^2  (use "x" for a root at 0)
        </Typography>

        <TextField
          fullWidth
          label="Polynomial Equation"
          value={equationInput}
          onChange={(e) => handleEquationChange(e.target.value)}
          error={!!parseError}
          helperText={parseError || 'Use ^ for exponents, e.g., (x-2)^2'}
          placeholder="f(x) = 1/6(x+3)(x-2)^2"
        />

        {/* LaTeX Preview */}
        {equationInput && !parseError && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview:
            </Typography>
            <Box sx={{ fontSize: '1.2rem', mt: 1 }}>
              <span dangerouslySetInnerHTML={{
                __html: katex.renderToString(equationToLatex(equationInput), { throwOnError: false })
              }} />
            </Box>
          </Box>
        )}
      </Paper>

      {/* Section 2: Intercept Type Selection */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          2. Choose Intercept Type
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={config.interceptType || 'x'}
            onChange={handleInterceptTypeChange}
          >
            <FormControlLabel
              value="x"
              control={<Radio />}
              label="X-intercepts (roots)"
            />
            <FormControlLabel
              value="y"
              control={<Radio />}
              label="Y-intercept"
              disabled={yInterceptModeBlocked}
            />
          </RadioGroup>
        </FormControl>

        {yInterceptModeBlocked && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            This polynomial passes through the origin (f(0) = 0), so its y-intercept
            lies on the x-axis. Y-intercept mode isn't available — use X-intercepts.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {config.interceptType === 'y'
            ? 'Students will find where the graph crosses the y-axis (at x=0)'
            : 'Students will find where the graph crosses the x-axis'}
        </Typography>
      </Paper>

      {/* Section 3: Parsed Information */}
      {parsedPolynomialDisplay && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            3. Parsed Polynomial Information
          </Typography>
          <ParsedPolynomialSummary polynomial={parsedPolynomialDisplay} />
        </Paper>
      )}

      {/* Section 4: Grid Configuration with Auto-Scaling Guidance */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          4. Grid Configuration
        </Typography>

        {/* AUTO-SCALING GUIDANCE */}
        {parsedPolynomialDisplay && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Suggested grid bounds:</strong> Based on your polynomial's roots and y-intercept,
              consider setting:
              <br />
              X range: [{suggestXMin(parsedPolynomialDisplay)}, {suggestXMax(parsedPolynomialDisplay)}]
              <br />
              Y range: [{suggestYMin(parsedPolynomialDisplay)}, {suggestYMax(parsedPolynomialDisplay)}]
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <TextField
            type="number"
            label="X Min"
            value={config.grid?.xMin ?? -10}
            onChange={(e) => handleGridChange('xMin', Number(e.target.value))}
          />
          <TextField
            type="number"
            label="X Max"
            value={config.grid?.xMax ?? 10}
            onChange={(e) => handleGridChange('xMax', Number(e.target.value))}
          />
          <TextField
            type="number"
            label="Y Min"
            value={config.grid?.yMin ?? -10}
            onChange={(e) => handleGridChange('yMin', Number(e.target.value))}
          />
          <TextField
            type="number"
            label="Y Max"
            value={config.grid?.yMax ?? 10}
            onChange={(e) => handleGridChange('yMax', Number(e.target.value))}
          />
        </Box>

        {/* Grid validation error */}
        {gridError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {gridError}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Tip: Ensure the grid is large enough to show key features (intercepts, turning points)
          but small enough that curves are distinguishable.
        </Typography>
      </Paper>

      {/* Section 5: Graph Preview */}
      {variations.length === 4 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            5. Generated Graph Variations
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Graph {variations.find(v => v.isCorrect)?.label} is the correct answer.
            All graphs {config.interceptType === 'y'
              ? 'pass through the same y-intercept'
              : 'pass through the same x-intercepts'}.
          </Alert>
          <PolynomialGraphPreview
            variations={variations}
            gridConfig={config.grid}
          />
        </Paper>
      )}
    </Box>
  );
};

/**
 * Display parsed polynomial information
 */
const ParsedPolynomialSummary = ({ polynomial }) => {
  const { factors = [], degree, leadingCoefficient, yIntercept } = polynomial;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography>
        <strong>Degree:</strong> {degree ?? '—'}
      </Typography>
      {leadingCoefficient != null && (
        <Typography>
          <strong>Leading Coefficient:</strong> {leadingCoefficient.toFixed(4)}
          {leadingCoefficient > 0 ? ' (positive)' : ' (negative)'}
        </Typography>
      )}
      <Typography>
        <strong>Roots (x-intercepts):</strong>
      </Typography>
      <Box sx={{ pl: 2 }}>
        {factors.length > 0 ? factors.map((f, i) => (
          <Typography key={i}>
            x = {f.root} (multiplicity {f.multiplicity}
            {f.multiplicity % 2 === 0 ? ' - bounces' : ' - crosses'})
          </Typography>
        )) : (
          <Typography color="text.secondary">None</Typography>
        )}
      </Box>
      <Typography>
        <strong>Y-intercept:</strong> {yIntercept != null ? `(0, ${yIntercept.toFixed(4)})` : '—'}
      </Typography>
    </Box>
  );
};

/**
 * Preview all 4 graph variations in a 2x2 grid
 */
const PolynomialGraphPreview = ({ variations, gridConfig }) => {
  // One shared y-compression across the whole variation set — per-curve scaling
  // drew curves with the same true y-intercept at different heights.
  const curveElements = polynomialsToCurveElements(
    variations.map((v) => v.polynomial),
    gridConfig
  );
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 2,
      }}
    >
      {variations.map((variation, index) => (
        <Paper
          key={variation.label}
          sx={{
            p: 1,
            border: variation.isCorrect ? '3px solid green' : '1px solid grey',
            bgcolor: variation.isCorrect ? 'success.light' : 'background.paper',
          }}
        >
          <Typography variant="subtitle2" align="center">
            Graph {variation.label}
            {variation.isCorrect && ' (Correct)'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CoordinateGrid
              config={gridConfig}
              elements={[curveElements[index]]}
              pendingPoints={[]}
              mode="point"
              readOnly={true}
              width={200}
              height={200}
            />
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default PolynomialGraphAdmin;
